import React, { Suspense, lazy } from "react";
import { Routes, Route } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import PrivateRoute from "./components/PrivateRoute";
import AdminProtectedRoute from "./components/AdminProtectedRoute";

// Layouts
const HomeLayout = lazy(() => import("./components/layouts/HomeLayout"));
const DashboardLayout = lazy(() =>
  import("./components/layouts/DashboardLayout")
);
const AdminLayout = lazy(() => import("./Admin/layout/AdminLayout"));

// Public Pages
const Home = lazy(() => import("./pages/Home"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Disclaimer = lazy(() => import("./pages/Disclaimer"));
const Blogs = lazy(() => import("./pages/Blogs"));
const BlogsFullpost = lazy(() =>
  import("./components/PageComponents/BlogsFullpost")
);
const Learn = lazy(() => import("./pages/Learn"));
const LearnFullpost = lazy(() =>
  import("./components/PageComponents/LearnFullpost")
);
const Resources = lazy(() => import("./pages/Resources"));
const ResourcesFullpost = lazy(() =>
  import("./components/PageComponents/ResourcesFullpost")
);
const FAQ = lazy(() => import("./pages/FAQ"));
const NotFound = lazy(() => import("./pages/NotFound")); // 404 Page

// Private User Pages
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Profile = lazy(() => import("./pages/Profile"));
const Courses = lazy(() => import("./pages/Courses"));
const CourseDetail = lazy(() =>
  import("./components/PageComponents/CourseDetail")
);

// Admin Pages
const AdminDashboard = lazy(() =>
  import("./Admin/AdminPages/AdminDashboard")
);
const AdminManageUsers = lazy(() =>
  import("./Admin/AdminPages/AdminManageUsers")
);
const AdminBlogs = lazy(() => import("./Admin/AdminPages/AdminBlogs"));
const AdminLearn = lazy(() => import("./Admin/AdminPages/AdminLearn"));
const AdminCourses = lazy(() => import("./Admin/AdminPages/AdminCourses"));
const AdminResources = lazy(() =>
  import("./Admin/AdminPages/AdminResources")
);
const AdminManageUsersDashboard = lazy(() =>
  import("./Admin/AdminPages/AdminManageUsersDashboard")
);

const App = () => {
  return (
    <AuthProvider>
      <LanguageProvider>
        <Suspense
          fallback={
            <div
              className="flex justify-center items-center min-h-screen"
              aria-label="Loading"
            >
              <div className="animate-spin rounded-full border-t-4 border-blue-500 h-16 w-16" />
            </div>
          }
        >
          <Routes>
            {/* Admin Routes */}
            <Route element={<AdminProtectedRoute />}>
              <Route element={<AdminLayout />}>
                <Route path="/admin/dashboard" element={<AdminDashboard />} />
                <Route path="/admin/users" element={<AdminManageUsers />} />
                <Route path="/admin/blogs" element={<AdminBlogs />} />
                <Route path="/admin/learn" element={<AdminLearn />} />
                <Route path="/admin/courses" element={<AdminCourses />} />
                <Route path="/admin/resources" element={<AdminResources />} />
                <Route
                  path="/admin/manage-dashboard"
                  element={<AdminManageUsersDashboard />}
                />
              </Route>
            </Route>

            {/* Public Standalone Pages */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/disclaimer" element={<Disclaimer />} />            

            {/* Public Pages with Home Layout */}
            <Route element={<HomeLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/blogs" element={<Blogs />} />
              <Route path="/blogs/:id" element={<BlogsFullpost />} />
              <Route path="/learn" element={<Learn />} />
              <Route path="/learn/:id" element={<LearnFullpost />} />
              <Route path="/resources" element={<Resources />} />
              <Route path="/resources/:id" element={<ResourcesFullpost />} />
              <Route path="/faq" element={<FAQ />} />
            </Route>

            {/* Private User Pages with Dashboard Layout */}
            <Route element={<PrivateRoute />}>
              <Route element={<DashboardLayout />}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/courses" element={<Courses />} />
                <Route path="/courses/:id" element={<CourseDetail />} />
                <Route path="/dashboard/blogs" element={<Blogs />} />
                <Route path="/dashboard/blogs/:id" element={<BlogsFullpost />} />
                <Route path="/dashboard/faq" element={<FAQ />} />
              </Route>
            </Route>

            {/* 404 Page - Fallback */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </LanguageProvider>
    </AuthProvider>
  );
};

export default App;
